/**
 * Spreadsheet export sanitisation — Formula / CSV Injection (CWE-1236).
 *
 * APTS report 101435 V1.0 (04-08-2026), observation #9; findings tracker #9.
 *
 * A Help Desk ticket whose text begins with `=`, `+`, `-` or `@` is written
 * verbatim into the exported .xlsx. Excel then treats that cell as a FORMULA,
 * not text, so opening the export executes it — the auditors demonstrated a
 * payload that rendered as a clickable link and navigated the officer's Excel
 * to an external site. HYPERLINK/WEBSERVICE (and DDE, in some configurations)
 * make this a path onto the workstation of whoever downloads the MIS report.
 *
 * The fix is applied at EXPORT time, not at input time: the stored value is
 * legitimate user text and must keep round-tripping through the UI unchanged.
 * Prefixing a cell with an apostrophe tells Excel "this is literal text"; the
 * apostrophe itself is not displayed and is not part of the cell value.
 *
 * SCOPE NOTE — this application exports through THREE distinct mechanisms, and
 * each needs a different call site:
 *   - DevExtreme `exportDataGrid` + ExcelJS  -> use sanitizeExcelCell() inside
 *     the `customizeCell` callback. There is no row array to map; exportDataGrid
 *     writes cells itself. If a component already has its own customizeCell,
 *     wrap it with withCellSanitizer() instead of replacing it.
 *   - `XLSX.utils.json_to_sheet(rows)`       -> use sanitizeRows() on the array
 *     immediately before the call. Rows are OBJECTS keyed by column.
 *   - ExcelJS `sheet.addRow([...])` /
 *     `sheet.addRows([[...]])`               -> use sanitizeArrayRow() /
 *     sanitizeArrayRows(). Rows here are ARRAYS, so sanitizeRows() does not
 *     apply — it would copy the array's numeric indices onto a plain object.
 */

/**
 * Characters that make Excel parse a cell as a formula rather than text.
 * Tab and carriage return are included: both are accepted as leading
 * whitespace by Excel's parser and are a documented bypass for filters that
 * only check `=`, `+`, `-` and `@`.
 */
const FORMULA_TRIGGER = /^[=+\-@\t\r]/;

/**
 * A leading `+` or `-` is overwhelmingly a phone number or a negative figure
 * in this application's exports — driver phone (`crw_ph`), mileage variance,
 * fuel deltas. Prefixing those would put a stray apostrophe into numeric MIS
 * columns that other systems ingest, which is a real operational problem.
 *
 * So `+` / `-` values are left alone ONLY when the remainder is plainly
 * numeric: digits with optional separators normally seen in phone numbers and
 * decimals. Anything else keeps the prefix.
 *
 *   "+919876543210" -> safe, untouched (phone)
 *   "-5.2"          -> safe, untouched (negative figure)
 *   "-2+3+cmd|'/C calc'!A0" -> NOT numeric, gets prefixed
 *
 * `=` and `@` are never legitimate leading characters for a number, so they
 * are always prefixed regardless of what follows.
 */
const BENIGN_NUMERIC = /^[+\-][\d\s().\-]*\d[\d\s().\-]*$/;

/**
 * Returns the value as it should be written to a spreadsheet cell.
 *
 * Non-strings (numbers, dates, booleans, null) are returned untouched so that
 * numerics stay real numbers in the sheet — that is what keeps this change
 * invisible to every legitimate export.
 */
export function sanitizeCellValue(value: any): any {
  if (typeof value !== 'string' || value.length === 0) { return value; }
  if (!FORMULA_TRIGGER.test(value)) { return value; }
  if (BENIGN_NUMERIC.test(value)) { return value; }
  return "'" + value;
}

/**
 * `customizeCell` handler for DevExtreme's exportDataGrid + ExcelJS.
 *
 * Usage:
 *   exportDataGrid({
 *     component: e.component,
 *     worksheet,
 *     customizeCell: sanitizeExcelCell
 *   })
 *
 * Accepts the DevExtreme options object and mutates `excelCell.value` in place,
 * which is the only way to influence what exportDataGrid writes.
 */
export function sanitizeExcelCell(options: any): void {
  if (!options || !options.excelCell) { return; }
  options.excelCell.value = sanitizeCellValue(options.excelCell.value);
}

/**
 * Sanitises an array of plain row objects before XLSX.utils.json_to_sheet().
 *
 * Returns NEW objects rather than mutating in place: several components build
 * their export array from the same list that is still bound to the on-screen
 * grid, and mutating it would put apostrophes into the live view.
 */
export function sanitizeRows<T extends Record<string, any>>(rows: T[]): T[] {
  if (!Array.isArray(rows)) { return rows; }
  return rows.map(row => {
    if (!row || typeof row !== 'object') { return row; }
    if (Array.isArray(row)) { return sanitizeArrayRow(row) as any; }
    const clean: Record<string, any> = {};
    Object.keys(row).forEach(key => { clean[key] = sanitizeCellValue(row[key]); });
    return clean as T;
  });
}

/**
 * Sanitises a single ARRAY-shaped row, for ExcelJS `sheet.addRow([...])`.
 *
 * sanitizeRows() cannot be used here: an array is `typeof 'object'`, so it
 * would be rebuilt as a plain object keyed "0", "1", "2" — ExcelJS would then
 * write a blank row and the export would silently lose data. Kept as its own
 * function so that mistake is not possible at a call site.
 *
 * Returns a NEW array; the caller's data is never mutated.
 */
export function sanitizeArrayRow(row: any[]): any[] {
  if (!Array.isArray(row)) { return row; }
  return row.map(cell => sanitizeCellValue(cell));
}

/**
 * Sanitises a single OBJECT-shaped row, for ExcelJS `sheet.addRow({ key: ... })`
 * where the sheet was defined with `worksheet.columns = [{ key: ... }]`.
 *
 * Returns a NEW object; the caller's source record is never mutated.
 */
export function sanitizeRow<T extends Record<string, any>>(row: T): T {
  if (!row || typeof row !== 'object' || Array.isArray(row)) { return row; }
  const clean: Record<string, any> = {};
  Object.keys(row).forEach(key => { clean[key] = sanitizeCellValue(row[key]); });
  return clean as T;
}

/** Array-of-arrays variant, for ExcelJS `sheet.addRows([[...], [...]])`. */
export function sanitizeArrayRows(rows: any[][]): any[][] {
  if (!Array.isArray(rows)) { return rows; }
  return rows.map(row => sanitizeArrayRow(row));
}

/**
 * Wraps an EXISTING `customizeCell` handler so sanitisation runs after it.
 *
 * Several grids will eventually need their own cell formatting. Passing
 * `customizeCell: sanitizeExcelCell` in that case would silently discard the
 * component's own handler; passing `withCellSanitizer(myHandler)` keeps both.
 *
 * Order matters: the component's handler runs FIRST, then sanitisation, so a
 * handler that assigns a formula-looking string into excelCell.value is still
 * neutralised rather than bypassing the guard.
 *
 * Usage:
 *   exportDataGrid({ component, worksheet, customizeCell: withCellSanitizer(myHandler) })
 */
export function withCellSanitizer(existing?: (options: any) => void): (options: any) => void {
  return function (options: any): void {
    if (typeof existing === 'function') { existing(options); }
    sanitizeExcelCell(options);
  };
}
